/* istanbul ignore file */

import { ApolloClient, Reference, ApolloLink } from '@apollo/client';
import { HttpLink } from '@apollo/client/link/http';
import { InMemoryCache } from '@apollo/client/cache';
import { fetch } from 'cross-fetch';

import { SentryLink } from 'apollo-link-sentry';

import middlewares from './middlewares';

import { CheckedOnboardingPinnedPostMapVar } from 'modules/post/components/PinnedPostList/CheckedOnboardingPinnedPostVar';
import { notificationLastSeenMapVar } from 'modules/notifications/components/NotificationBell/NotificationLastSeenVar';
import { taskItemStatusMapVar } from 'lib/tiptap-plugins/TaskItemStatusVar';
import { env } from 'utils/env';

const httpLink = new HttpLink({
  uri: env.GRAPHQL_BASE_URL,
  fetch,
});

const mergeArrays = (
  originArray: any[] = [],
  addedArray: any[] = [],
  readField: any,
  mergeObjects: any
): any[] => {
  const mergedArray = [...originArray];

  addedArray.forEach((addedItem: Reference) => {
    const index = originArray.findIndex((originItem: Reference) => {
      return readField('id', originItem) === readField('id', addedItem);
    });

    if (index === -1) {
      mergedArray.push(addedItem);
    } else {
      mergedArray[index] = mergeObjects(originArray[index], addedItem);
    }
  });

  return mergedArray;
};

const mergeLearningCards = (
  existing: any,
  incoming: any,
  args: Record<string, any> | null,
  key: 'learningCards'
) => {
  if (args?.offset === 0) return { ...incoming };

  let merged = existing && existing[key] ? [...existing[key]] : [];

  const end = args?.offset + Math.min(args?.limit, incoming[key].length);

  for (let i = args?.offset; i < end; ++i) {
    merged[i] = incoming[key][i - args?.offset];
  }

  const limit = incoming[key]?.length === 0 ? 0 : incoming.limit;
  const offset =
    existing?.offset > args?.offset ? existing.offset : args?.offset;

  return { ...incoming, limit, offset, [key]: merged };
};

const cache = new InMemoryCache({
  typePolicies: {
    User: {
      // user
      keyFields: ['userId'],
    },
    ClassCodeDto: {
      /**
       * mutation 후 code 자동 업데이트 하려면 id가 필요
       * ClassCodeDto에 id가 없어서 classroomId를 id로 대체
       */
      keyFields: ['classroomId'],
    },
    PostDto: {
      fields: {
        reactions: {
          merge(existing, incoming) {
            // Better, but not quite correct.
            return incoming;
          },
        },
      },
    },
    CommentDto: {
      fields: {
        reactions: {
          merge(existing, incoming) {
            // Better, but not quite correct.
            return incoming;
          },
        },
      },
    },
    ReplyDto: {
      fields: {
        reactions: {
          merge(existing, incoming) {
            // Better, but not quite correct.
            return incoming;
          },
        },
      },
    },
    Query: {
      fields: {
        notificationLastSeen: {
          read(_, { variables }) {
            if (!variables?.userId) {
              return;
            }

            const lastSeenByUserId =
              notificationLastSeenMapVar()[variables?.userId];
            return lastSeenByUserId ?? null;
          },
        },

        getCheckedOnboardingPinnedPost: {
          read(_, { variables }) {
            if (!variables?.userId || !variables?.classroomId) {
              return;
            }

            const CheckedOnboardingPinnedPostUserId =
              CheckedOnboardingPinnedPostMapVar()[variables?.userId];

            const isChecked =
              CheckedOnboardingPinnedPostUserId?.classroomIds?.includes(
                variables?.classroomId
              ) ?? false;

            return {
              isChecked,
            };
          },
        },

        taskItemStatus: {
          read(_, { variables }) {
            if (!variables?.taskItemId || !variables?.userId) {
              return null;
            }
            const status =
              taskItemStatusMapVar()[variables.userId]?.[variables.taskItemId];
            return status ?? null;
          },
        },
        listLearningCards: {
          keyArgs: ['authorId'],
          merge(existing, incoming, { args }) {
            return mergeLearningCards(
              existing,
              incoming,
              args,
              'learningCards'
            );
          },
        },
        listLearningCommunityCards: {
          keyArgs: false,
          merge(existing, incoming, { args }) {
            return mergeLearningCards(
              existing,
              incoming,
              args,
              'learningCards'
            );
          },
        },
        listLearningSchoolCards: {
          keyArgs: ['schoolId'],
          merge(existing, incoming, { args }) {
            return mergeLearningCards(
              existing,
              incoming,
              args,
              'learningCards'
            );
          },
        },
        listChannelMediaPosts: {
          keyArgs: ['classroomId', 'channelId'],
          merge(
            existing = {
              __typename: 'PostsSearchCollectionDto',
              posts: [],
            },
            incoming,
            { mergeObjects, readField, args }
          ) {
            let merged = [...existing.posts];

            if (args?.page > 1) {
              merged = mergeArrays(
                existing.posts,
                incoming.posts,
                readField,
                mergeObjects
              );
            } else {
              merged = mergeArrays(
                incoming?.posts,
                existing.posts,
                readField,
                mergeObjects
              );
            }

            return {
              ...incoming,
              posts: merged,
            };
          },
        },
        listClassroomMediaPosts: {
          keyArgs: ['classroomId'],
          merge(
            existing = {
              __typename: 'PostsSearchCollectionDto',
              posts: [],
            },
            incoming,
            { mergeObjects, readField, args }
          ) {
            let merged = [...existing.posts];

            if (args?.page > 1) {
              merged = mergeArrays(
                existing.posts,
                incoming.posts,
                readField,
                mergeObjects
              );
            } else {
              merged = mergeArrays(
                incoming?.posts,
                existing.posts,
                readField,
                mergeObjects
              );
            }

            return {
              ...incoming,
              posts: merged,
            };
          },
        },
      },
    },
  },
});

const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    ...middlewares,
    // 순서 유지해야함, httpLink 보다 SentryLink가 먼저 설정되어야 함
    new SentryLink({
      uri: env.GRAPHQL_BASE_URL,
      attachBreadcrumbs: {
        includeVariables: true,
        includeError: true,
      },
    }),
    httpLink,
  ]),
  cache,
  connectToDevTools: env.STAGE !== 'prod',
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      // https://github.com/apollographql/apollo-client/issues/6833#issuecomment-679446789
      nextFetchPolicy(lastFetchPolicy) {
        if (
          lastFetchPolicy === 'cache-and-network' ||
          lastFetchPolicy === 'network-only'
        ) {
          return 'cache-first';
        }
        return lastFetchPolicy;
      },
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  },
});

export default apolloClient;
